import { FC } from 'react';
import {
  MobileContainer,
  StatsContainer,
  StyledAllieChip,
  StyledIconButton,
  VariableValueContainer,
} from '../../ProcessVariableCard.styles';
import {
  ContentProcessVariableProps,
  StatusProcessVariable,
} from '../../ProcessVariableCard.types';
import { AllieText } from '../../../../../components';
import { AllieWarningOutlineIcon, AllieEditPencilIcon } from '../../../../../styling/icons';
import { AllieColors } from '../../../../../common/color.types';
import { ContentOutdated } from '../ContentOutdated';
import { VariableTagsRow } from '../VariableTagsRow';

interface ContentExpandedVariableMobileProps extends ContentProcessVariableProps {
  name: string;
  lastChangeAt: string;
  labelValues: string;
  backgroundColor: AllieColors;
  status: StatusProcessVariable;
  textOutdated: string;
  tags?: { id: number; name: string }[];
  onEditTags?: () => void;
  editTagsAriaLabel?: string;
}

export const ContentExpandedVariableMobile: FC<ContentExpandedVariableMobileProps> = ({
  fontColor,
  graphTrendPreview,
  machineName,
  value,
  borderChipColor,
  statusText,
  name,
  lastChangeAt,
  labelValues,
  backgroundColor,
  status,
  textOutdated,
  isMobile,
  avgLabel,
  avgValue,
  minLabel,
  minValue,
  maxLabel,
  maxValue,
  withoutDataLabel,
  isCustomType,
  isMetricOnOff,
  tags,
  onEditTags,
  editTagsAriaLabel,
}) => {
  return (
    <MobileContainer>
      <div className="header">
        <AllieText color="neutral-70" type="body1">
          {machineName}
        </AllieText>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <AllieText color="neutral-70" type="button-normal">
              {name}
            </AllieText>
            {onEditTags && (
              <StyledIconButton
                size="xs"
                aria-label={editTagsAriaLabel}
                onClick={(e: any) => {
                  e.stopPropagation();
                  onEditTags();
                }}
              >
                <AllieEditPencilIcon />
              </StyledIconButton>
            )}
          </div>
          {status === 'outdated' && (
            <StyledAllieChip
              outline
              corner="rounded"
              style={{
                border: `1.5px solid var(--allie-color-${borderChipColor})`,
                cursor: 'default',
              }}
            >
              <AllieText type="body3" color={fontColor} weight={500} style={{ textWrap: 'nowrap' }}>
                {statusText}
              </AllieText>
            </StyledAllieChip>
          )}
        </div>
        {tags && tags.length > 0 && <VariableTagsRow tags={tags} />}
      </div>
      <VariableValueContainer $backgroundColor={backgroundColor} $isMobile={isMobile}>
        <div className="status-value-container-mobile">
          <div>
            <AllieText color="neutral-70" type="body3" weight={600}>
              {labelValues}
            </AllieText>
            {status !== 'outdated' && (
              <div className="value-container">
                <AllieText color={fontColor} type="h3" style={{ fontSize: 30, lineHeight: '38px' }}>
                  {value}
                </AllieText>
                <AllieWarningOutlineIcon
                  style={{ fontSize: '24px', color: `var(--allie-color-${fontColor})` }}
                />
              </div>
            )}
          </div>
          {status !== 'outdated' && (
            <div className="chip-container">
              <>
                <StyledAllieChip
                  outline
                  corner="rounded"
                  style={{
                    border: `1.5px solid var(--allie-color-${borderChipColor})`,
                    cursor: 'default',
                  }}
                >
                  <AllieText
                    type="body3"
                    color={fontColor}
                    weight={500}
                    style={{ textWrap: 'nowrap' }}
                  >
                    {statusText}
                  </AllieText>
                </StyledAllieChip>
                <AllieText color="neutral-50" type="field3">
                  {lastChangeAt}
                </AllieText>
              </>
            </div>
          )}
        </div>
        <StatsContainer
          isMobile={isMobile}
          isCustomType={isCustomType!}
          isMetricOnOff={isMetricOnOff!}
        >
          <div>
            <AllieText color="neutral-70" type="body2">
              {avgLabel}
            </AllieText>
            <AllieText color="neutral-70" type="h4">
              {avgValue}
            </AllieText>
          </div>
          <div>
            <AllieText color="neutral-70" type="body2">
              {minLabel}
            </AllieText>
            <AllieText color="neutral-70" type="h4">
              {minValue}
            </AllieText>
          </div>
          <div>
            <AllieText color="neutral-70" type="body2">
              {maxLabel}
            </AllieText>
            <AllieText color="neutral-70" type="h4">
              {maxValue}
            </AllieText>
          </div>
        </StatsContainer>
      </VariableValueContainer>
      {status === 'outdated' ? (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <ContentOutdated text={textOutdated} title={withoutDataLabel} />
          <AllieText
            color="neutral-50"
            type="field3"
            style={{ display: 'flex', justifyContent: 'center' }}
          >
            {lastChangeAt}
          </AllieText>
        </div>
      ) : (
        <div className="graph-container">{graphTrendPreview}</div>
      )}
    </MobileContainer>
  );
};
